import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { IMultiplyModel } from '@src/slide/domain/ui-model/element-modeles/Interface/multiply-model.interface';
import { IGroupModel } from '@src/slide/domain/ui-model/element-modeles/Interface/group-model.interface';
import { ITableModel } from '@src/slide/domain/ui-model/element-modeles/Interface/table-model.interface';
import {
    IExcludeValues,
    INoConflictProperty,
    TGetPropertyFunc,
} from '@src/slide/domain/ui-model/element-modeles/property/utils/merge-property/merge-property.interface';
import { WrapType } from '@src/slide/domain/ui-model/element-modeles/property/text/constants/text-style-type';
import {
    ExcludeBaseBorderProps,
    ExcludeMoreBorderProps,
    ExcludeBackgroundProps,
} from '@src/slide/domain/ui-model/element-modeles/constant/props';
import { copy, objectCompareLite } from '@src/slide/infrastructure/module/independent/object-operation';
import { enumerateGroupItems } from '@src/slide/domain/ui-model/element-modeles/property/group-operation/enumerate';
import { EFFECT_KEY } from '@src/slide/domain/ui-model/element-modeles/property/effect/effect.interface';
import { BORDER_KEY } from '@src/slide/domain/ui-model/element-modeles/property/border/border.interface';
import { FILL_KEY } from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import { LOCK_KEY } from '@src/slide/domain/ui-model/element-modeles/property/lock/lock.interface';
import {
    FONT_FILL_KEY,
    FONT_BORDER_KEY,
    FONT_EFFECT_KEY,
    SUBSCRIPT_KEY,
    SUPERSCRIPT_KEY,
} from '@src/slide/domain/ui-model/element-modeles/property/text/constants';
import { BORDER_SKETCH_KEY } from '@src/slide/domain/ui-model/element-modeles/property/border/constants';
import { ISlideModel } from '../../../Interface/slide-model.interface';

/**
 * 由src/slide/domain/ui-model/element-modeles/multiply-model.ts迁移而来
 * 将组合uiModel|多选uiModel|表格uiModel的所有子元素某些相同值的属性进行合并保留，不同的则忽略
 * 第一层如果一方有一方没有则取合集，都有则进行后续层级判断。
 * 后续层级的merge规则根据不同属性类型做区分,比如effect会再进行一层的合并操作，而普通的只要不完全一样则将第一层置为undefined
 * @param model 组合uiModel|多选uiModel|表格uiModel
 * @param getPropertyFunc 读取uiModel上属性值的方法
 * 注意：getPropertyFunc返回值的第一层必须为属性名 例如 { effect: { ... } }  (因为内部handleMergeValue会根据属性名做不同的逻辑判断)
 */
export function mergeNoConflictProperty(
    model: IMultiplyModel | IGroupModel | ITableModel,
    getPropertyFunc: TGetPropertyFunc,
): INoConflictProperty {
    const children = getChildren(model);
    const propertyValArr = getPropertyValArr(children, getPropertyFunc);
    return mergeMultiItemValues(propertyValArr, getExcludeValues(children));
}

// 互斥的属性对，同组属性值不能同时为true，如果同时为 true，需要都清除
const mutuallyExclusiveProps = [
    [SUBSCRIPT_KEY, SUPERSCRIPT_KEY],
];

/**
 * 过滤给定对象中互斥的属性值
 * @param originObject
 * @returns
 */
function filterMutuallyExclusiveProps(originObject: object | null) {
    if (!originObject) {
        return originObject;
    }
    if (Object.keys(originObject).length === 0) {
        return originObject;
    }
    const filteredObject = { ...originObject };
    mutuallyExclusiveProps.forEach((props) => {
        // 获取当前互斥属性对中值为 true 的属性
        const trulyProps = props.filter(prop => originObject[prop]);
        if (trulyProps.length > 1) {
            // 当前互斥属性对中值为 true 的属性多于一个，按照互斥原则，需要清除这些属性
            props.forEach((prop) => {
                if (Object.hasOwnProperty.call(originObject, prop)) {
                    filteredObject[prop] = undefined;
                }
            });
        }
    });
    return filteredObject;
}

/**
 * 将属性对象数组合并为一个对象，只保留相同的(multiply-model.ts迁移而来)
 * @param itemValuesArray 属性对象数组合
 * @param excludeValues 合并时判定值是否相同需要忽略的子属性
 */
export function mergeMultiItemValues(
    itemValuesArray: INoConflictProperty[],
    excludeValues?: IExcludeValues,
): INoConflictProperty {
    let mergedValues = null;
    if (Array.isArray(itemValuesArray)) {
        for (let i = 0; i < itemValuesArray.length; i++) {
            const values = copy(itemValuesArray[i], true);
            if (!mergedValues) {
                mergedValues = values;
            } else {
                const allKeys = Object.keys(values);
                for (let j = 0; j < allKeys.length; j++) {
                    const key = allKeys[j];
                    const excludeProps = excludeValues ? excludeValues[key] : null;
                    // 如果两者存在相同的key，则计算合并值，value相同保留原值，不同置为undefined
                    if (Object.hasOwnProperty.call(mergedValues, key)) {
                        const equal = objectCompareLite(
                            // @ts-ignore
                            mergedValues[key],
                            values[key],
                            excludeProps,
                        );

                        // 计算合并后的值
                        if (!equal) {
                            handleMergeValue(key, values, mergedValues);
                        }
                    } else {
                        // @ts-ignore
                        mergedValues[key] = values[key];
                    }
                }
            }
        }
    }
    return filterMutuallyExclusiveProps(mergedValues) || {};
}

/**
 * 合并时判定值是否相同需要忽略的子属性(multiply-model.ts迁移而来)
 * @param selectedModels
 */
export function getExcludeValues(selectedModels: IElementBaseModel[]): IExcludeValues {
    const excludeValues = {};
    const notAllConnector = Array.isArray(selectedModels)
        && selectedModels.some(model => model.type !== UIModelType.CONNECTOR);
    excludeValues[FILL_KEY] = ExcludeBackgroundProps;
    excludeValues[BORDER_KEY] = !notAllConnector ? ExcludeBaseBorderProps : ExcludeMoreBorderProps;
    return excludeValues;
}

/**
 * 将组合|多选|表格 拆分成子元素数组
 * @param model 组合uiModel|多选uiModel|表格uiModel
 */
function getChildren(model: IMultiplyModel | IGroupModel | ITableModel): IElementBaseModel[] {
    let children: IElementBaseModel[] = [];
    switch (model.type) {
        case UIModelType.MULTIPLY:
            // 过滤出所有非 table 和 chart 的
            children = (model as IMultiplyModel).selectedModels.filter(item =>
                item.type !== UIModelType.TABLE && item.type !== UIModelType.CHART);
            break;
        case UIModelType.GROUP:
            // 打平所有层级
            enumerateGroupItems(model as IGroupModel, item => children.push(item), [UIModelType.GROUP]);
            break;
        case UIModelType.TABLE:
            // 选中的单元格的
            children = (model as ITableModel).selectedCellRanges
                .map(item => (model as ITableModel).tableItems[item.rowStart][item.colStart]);
            break;
        default:
            children = [];
    }
    return children;
}

/**
 * 获取uiModel数组的某个属性集合
 * @param models uiModel数组
 * @param getPropertyFunc 从uiModel上读取出对应属性的方法，比如effect
 * 注意：getPropertyFunc返回值的第一层必须为属性 例如 { effect: { ... } }
 */
function getPropertyValArr(
    models: IElementBaseModel[],
    getPropertyFunc: TGetPropertyFunc,
): INoConflictProperty[] {
    if (!Array.isArray(models) || typeof getPropertyFunc !== 'function') {
        return [];
    }
    return models.map(item => getPropertyFunc(item) || {});
}

/**
 * (multiply-model.ts迁移而来)
 * @param key 属性名
 * @param values 属性对象B
 * @param mVlues 属性对象A
 */
// eslint-disable-next-line complexity
function handleMergeValue(key: string, values: INoConflictProperty, mVlues: INoConflictProperty) {
    const mergedValues = mVlues;
    const fontStyles = ['bold', 'italic', 'underLine', 'deleteLine'];
    const numberStyles = ['itemNumber', 'digitNumber'];
    if (key === 'font') {
        handleFontMergeValue(key, values, mergedValues);
    } else if (key === 'fontSize') {
        handleFontSizeMergeValue(key, values, mergedValues);
    } else if (key === EFFECT_KEY || key === FONT_EFFECT_KEY || key === BORDER_KEY) {
        handleEffectMergeValue(key, values, mergedValues);
    } else if (key === 'text-adjust' || key === LOCK_KEY) {
        handleTextAdjustMergeValue(key, values, mergedValues);
    } else if (key === FONT_BORDER_KEY || key === FONT_FILL_KEY) {
        handleFontBorderMergeValue(key, values, mergedValues);
    } else if (fontStyles.indexOf(key) >= 0) {
        mergedValues[key] = false;
    } else {
        mergedValues[key] = undefined;
    }
}

/**
 * 处理Font(multiply-model.ts迁移而来)
 * @param key
 * @param values
 * @param mVlues
 */
function handleFontMergeValue(key, values, mVlues) {
    const mergedValues = mVlues;
    if (values[key] && values[key].length > 0) {
        if (mergedValues[key] && mergedValues[key].length > 0) {
            mergedValues[key] = 'multi';
        } else {
            mergedValues[key] = values[key];
        }
    }
}

/**
 * 处理FontSize(multiply-model.ts迁移而来)
 * @param key
 * @param values
 * @param mVlues
 */
function handleFontSizeMergeValue(key, values, mVlues) {
    const mergedValues = mVlues;
    if (values[key] && values[key].length > 0) {
        if (mergedValues[key] && mergedValues[key].length > 0) {
            const newFontSize = values[key].match(/[0-9]+([.]{1}[0-9]+){0,1}/);
            const oldFontSize = mergedValues[key].match(/[0-9]+([.]{1}[0-9]+){0,1}/);
            if (newFontSize && oldFontSize) {
                mergedValues[key] = `font-size-${Math.min(newFontSize[0], oldFontSize[0])}+pt`;
            } else {
                mergedValues[key] = (newFontSize && values[key])
                    || (oldFontSize || mergedValues[key]);
            }
        } else {
            mergedValues[key] = values[key];
        }
    }
}

/**
 * 处理Effect数据(multiply-model.ts迁移而来)
 * @param key
 * @param values
 * @param mergedValues
 */
function handleEffectMergeValue(key, values, mergedValues) {
    const mergedValuesKeys = Object.keys(mergedValues[key] || {});
    mergedValuesKeys.forEach((k) => { /** k 是具体的effect名字：shadow, reflection, glow, softEdge */
        if (values[key]?.[k]) {
            // 对比 mergedValues[key][k] 和 values[key][k], 不相同也得删除
            const isObjEqual = objectCompareLite(values[key][k], mergedValues[key][k]);
            if (!isObjEqual) {
                // border SketchStyle 特殊处理，type 相同则保留
                if (k === BORDER_SKETCH_KEY
                    && (values[key]?.[k]?.type && values[key]?.[k]?.type === mergedValues[key]?.[k]?.type)) {
                    return;
                }
                Reflect.deleteProperty(mergedValues[key], k);
            }
        } else {
            Reflect.deleteProperty(mergedValues[key], k);
        }
    });
}

function handleFontBorderMergeValue(key, values, mergedValues) {
    mergedValues[key] = 'multi';
}

/**
 * merge 处理文字格式数据(multiply-model.ts迁移而来)
 * @param key
 * @param values
 * @param mValues
 */
export function handleTextAdjustMergeValue(key, values, mValues) {
    const mergedValues = mValues;
    const mergedValuesKeys = Object.keys(mergedValues[key] || {});
    mergedValuesKeys.forEach((mKey) => {
        if (values[key]?.[mKey]) {
            const isEqual = objectCompareLite(values[key][mKey], mergedValues[key][mKey]);
            if (isEqual) {
                mergedValues[key][mKey] = values[key][mKey];
            } else if (mKey === 'rawData') {
                // 处理padding， 合并时取大值
                const rawData = mValues[key][mKey];
                const rawDataKeys = Object.keys(rawData || {});
                rawDataKeys.forEach((rawDataKey) => {
                    if (mergedValues[key][mKey][rawDataKey] < values[key][mKey][rawDataKey]) {
                        mergedValues[key][mKey][rawDataKey] = values[key][mKey][rawDataKey];
                    }
                });
            } else {
                mergedValues[key][mKey] = WrapType.MULTI;
            }
        } else {
            mergedValues[key][mKey] = WrapType.MULTI;
        }
    });
}

/**
 * 获取所有的子元素，如果有组合，则遍历组合子元素
 * @param selectedModel
 * @param shouldExceptGroup 遍历组合时，是否要排除组合本身
 * @returns
 */
export const getAllChildModels = (
    selectedModel: IElementBaseModel | IMultiplyModel,
    shouldExceptGroup = true,
): IElementBaseModel[] => {
    const selectedModels: IElementBaseModel[] = [];
    if (selectedModel?.type === UIModelType.MULTIPLY) {
        (selectedModel as IMultiplyModel).selectedModels.forEach((model: IElementBaseModel) => {
            model.type === UIModelType.GROUP
                ? selectedModels.push(...recurseChildModels(model as IGroupModel, shouldExceptGroup))
                : selectedModels.push(model);
        });
    } else if (selectedModel?.type === UIModelType.GROUP) {
        if (!shouldExceptGroup) {
            selectedModels.push(selectedModel as IGroupModel);
        }
        selectedModels.push(...recurseChildModels(selectedModel as IGroupModel, shouldExceptGroup));
    } else {
        selectedModels.push(selectedModel as IElementBaseModel);
    }
    return selectedModels;
};

/**
 * groupModel下递归的所有子元素
 * @param shouldExceptGroup 是否排除掉子groupModel
*/
export const recurseChildModels = (groupModel: IGroupModel, shouldExceptGroup): IElementBaseModel[] => {
    const childModels: Array<any> = [];
    enumerateGroupItems(groupModel, (groupItemModel: IElementBaseModel) => {
        childModels.push(groupItemModel);
    }, shouldExceptGroup ? [UIModelType.GROUP] : null);
    if (childModels.length === 0) {
        return [];
    }
    return childModels;
};
